import { Grid, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useMessage } from "hooks/intl";
import { NavLink } from "react-router-dom";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { selectGlobalSearch, setGlobalSearch } from "reduxState/search/search";
import {
  CategoryDetailsType,
  CategorySubType,
  CategorySubTypeUnion,
  CategoryType,
  CategoryTypeUnion,
  StoredSearch,
} from "types/sidebar";
import { links } from "utilities/links";
import { useStyles } from "./styles";

interface Props {
  category: CategoryDetailsType;
}

const getItemDetails = (
  type: CategoryTypeUnion,
  item: any,
  subType?: CategorySubTypeUnion,
) => {
  switch (type) {
    case CategoryType.CAMPAIGN:
      return {
        link: links.campaign(item.campaignId),
        label: item.name,
      };
    case CategoryType.MEMBER:
      return {
        link: links.singleMemberGeneralInfo(item.customerId),
        label: subType ? item[subType] : `${item.firstName} ${item.lastName}`,
      };
    case CategoryType.ACHIEVEMENT:
      return {
        link: links.editAchievement(item.achievementId),
        label: item.name,
      };
    case CategoryType.TIER:
      const isTierSet = item.tierSet;

      return {
        link: isTierSet
          ? links.tierSet(item.tierSet.tierSetId)
          : links.editTier(item.levelId),
        label: item.name,
      };
    case CategoryType.WALLET:
      return {
        link: links.editWalletType(item.walletTypeId),
        label: item.name,
      };
    case CategoryType.REWARD:
      return {
        link: links.reward(item.rewardId),
        label: item.name,
      };
    case CategoryType.COLLECTION:
      return {
        link: links.collection(item.groupOfValuesId),
        label: item.name,
      };
  }
};

export const Category = ({ category }: Props) => {
  const { message } = useMessage();
  const classes = useStyles();
  const { code } = useAppSelector(selectCurrentStoreData);
  const globalSearchData = useAppSelector(selectGlobalSearch);
  const dispatch = useAppDispatch();

  const onItemClicked = (item: StoredSearch) => {
    const lastSearchedItems = globalSearchData[code];
    const storedSearch: StoredSearch[] = lastSearchedItems
      ? [...lastSearchedItems]
      : [];
    const isElementAlreadyInList = storedSearch.findIndex(
      (searchedItem) => searchedItem.link === item.link,
    );

    if (isElementAlreadyInList > -1) {
      storedSearch.splice(isElementAlreadyInList, 1);
    }

    storedSearch.unshift(item);

    const globalSearch = {
      ...globalSearchData,
      [code]: storedSearch.slice(0, 4),
    };

    return dispatch(setGlobalSearch(globalSearch));
  };

  return (
    <Grid container>
      <Typography className={classes.categoryTitle}>
        {message({ id: category.label })}
      </Typography>
      <Grid container>
        {category.items.map((item, index) => {
          const details = getItemDetails(category.type, item, item.subType);

          return (
            <Grid item xs={12} key={index} className={classes.categoryItem}>
              {details && (
                <NavLink
                  target="_blank"
                  to={details.link}
                  onClick={() =>
                    onItemClicked({
                      ...details,
                      category: category.label,
                    })
                  }
                >
                  {details.label}{" "}
                  {item?.subType ? (
                    <span className={classes.subType}>
                      {message({
                        id:
                          item.subType === CategorySubType.PHONE
                            ? "phoneNumber"
                            : item.subType,
                      })}
                    </span>
                  ) : null}
                </NavLink>
              )}
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
};
